import React, { useState, useEffect } from 'react';
import Card from '../../components/Card/Card';
import Badge from '../../components/Badge/Badge';
import DataTable from '../../components/DataTable/DataTable';
import Button from '../../components/Button/Button';
import { FiTrendingUp, FiAlertOctagon, FiDollarSign, FiAward, FiDownload, FiUsers, FiClock } from 'react-icons/fi';
import api from '../../api';
import './Reports.css';

const Reports = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reports.php');
      const json = res.data;
      setData(json);
    } catch (err) {
      console.error("Report Fetch Failed", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    const day = d.getDate().toString().padStart(2, '0');
    const month = d.toLocaleString('en-GB', { month: 'short' });
    const year = d.getFullYear();
    return `${day} ${month}, ${year}`;
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleExportCSV = () => {
    if (!data) return;
    const headers = ["Summary", "Value"];
    const rows = [
      ["Total Fine Collected", `INR ${data.totalFine}`],
      ["Total Overdue Books", data.overdueDetails.length],
      ["Generated At", data.generatedAt]
    ];
    
    let csvContent = "data:text/csv;charset=utf-8," 
      + headers.join(",") + "\n"
      + rows.map(r => r.join(",")).join("\n");
      
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `LMS_Executive_Summary_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading || !data) return <div className="loading-screen">Generating system insights...</div>;

  return (
    <div className="reports-page">
      <div className="page-header">
        <div>
          <h2 className="page-header__title">Library Insights & Reports</h2>
          <p className="page-header__sub">Deep analysis of inventory and member engagement</p>
        </div>
        <div className="report-actions">
           <Button variant="outline" icon={<FiDownload />} onClick={handleExportCSV}>Export Summary</Button>
        </div>
      </div>

      {/* KPI Stats Panel */}
      <div className="reports-kpi-grid">
         <Card padding="md" className="report-stat-card r-stat--revenue">
            <div className="r-stat-icon"><FiDollarSign /></div>
            <div className="r-stat-content">
               <p className="r-label">Collected Fine</p>
               <h3 className="r-value">₹{data.totalFine.toLocaleString()}</h3>
               <p className="r-info">Total Revenue to date</p>
            </div>
         </Card>
         <Card padding="md" className="report-stat-card r-stat--overdue">
            <div className="r-stat-icon"><FiAlertOctagon /></div>
            <div className="r-stat-content">
               <p className="r-label">Pending Losses</p>
               <h3 className="r-value">-{data.overdueDetails.length}</h3>
               <p className="r-info">Unreturned volumes</p>
            </div>
         </Card>
         <Card padding="md" className="report-stat-card r-stat--growth">
            <div className="r-stat-icon"><FiTrendingUp /></div>
            <div className="r-stat-content">
               <p className="r-label">Last Synchronization</p>
               <h3 className="r-value" style={{fontSize: '18px'}}>{new Date(data.generatedAt).toLocaleTimeString()}</h3>
               <p className="r-info">System is up to date</p>
            </div>
         </Card>
      </div>

      <div className="reports-grid-main">
         {/* Left Column: Overdue List */}
         <div className="reports-left">
            <Card padding="none" className="overdue-report-panel">
               <div className="panel-header">
                  <div>
                    <h3 className="panel-header__title">Urgent Overdue List</h3>
                    <p className="panel-header__sub">Members holding books past due dates</p>
                  </div>
                  <FiClock className="panel-header-icon" />
               </div>
               <div style={{maxHeight:'500px', overflow:'auto'}}>
                 <DataTable 
                    columns={[
                       { key: 'MemberName', label: 'Member' },
                       { key: 'BookTitle', label: 'Book Name' },
                       { key: 'DueDate', label: 'Due Date', render: (val) => <span style={{color:'var(--danger)', fontWeight:600}}>{formatDate(val)}</span> },
                       { key: 'Fine', label: 'Est. Fine', render: (val) => <span style={{fontWeight:700, color:'var(--danger)'}}>₹{val}</span> }
                    ]}
                    data={data.overdueDetails}
                 />
               </div>
            </Card>
         </div>

         {/* Right Column: Top Performers */}
         <div className="reports-right">
            <Card padding="md" className="ranking-panel">
               <div className="ranking-header">
                  <FiAward className="header-icon" />
                  <h4>Most Popular Books</h4>
               </div>
               <div className="ranking-list">
                  {data.topBooks.map((b, i) => (
                     <div key={i} className="ranking-item">
                        <span className="rank-num">{i+1}</span>
                        <div className="rank-info"><strong>{b.Title}</strong><p>By {b.Author}</p></div>
                        <Badge variant="primary">{b.BorrowCount} loans</Badge>
                     </div>
                  ))}
               </div>
            </Card>

            <Card padding="md" className="ranking-panel" style={{marginTop:20}}>
               <div className="ranking-header">
                  <FiUsers className="header-icon" />
                  <h4>Top Active Members</h4>
               </div>
               <div className="ranking-list">
                  {data.topMembers.map((m, i) => (
                     <div key={i} className="ranking-item">
                        <span className="rank-num">{i+1}</span>
                        <div className="rank-info"><strong>{m.Name}</strong><p>{m.MemberType}</p></div>
                        <Badge variant="success">{m.IssueCount} loans</Badge>
                     </div>
                  ))}
               </div>
            </Card>
         </div>
      </div>

      {/* Genre Distribution Charts */}
      <Card padding="md" style={{marginTop:20}}>
          <h4 className="chart-title">Content Volume by Category</h4>
          <div className="category-bars">
             {data.genreDistribution.map((cat, i) => {
               const total = data.genreDistribution.reduce((acc, c) => acc + parseInt(c.value), 0);
               const pct = total > 0 ? (parseInt(cat.value) / total * 100).toFixed(1) : 0;
               return (
                  <div key={i} className="cat-bar-item">
                     <div className="cat-bar-label"><span>{cat.label || 'Uncategorized'}</span> <span>{cat.value} books ({pct}%)</span></div>
                     <div className="cat-bar-track"><div className="cat-bar-fill" style={{width: `${pct}%`, background: `hsl(${i * 60 + 200}, 70%, 55%)`}}></div></div>
                  </div>
               );
             })}
          </div>
      </Card>
    </div>
  );
};

export default Reports;
